import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select'
import { Search, Calendar, Mail, Shield, Eye, ChevronLeft, ChevronRight } from 'lucide-react'
import { API_BASE_URL } from '../../../lib/config'
import { formatISTDate } from '../../../components/lib/dateUtils'
import { AdminPanelButton } from './AdminPanelButton'

const PER_PAGE = 10

interface UserData {
  id: string
  username: string
  email: string
  firstName?: string
  lastName?: string
  role?: string
  isActive?: boolean
  createdAt: string
  chatCount: number
  messageCount: number
  lastActive?: string
}

type SortOption = 'name' | 'username' | 'createdAt' | 'chatCount' | 'messageCount' | 'lastActive'

function getDisplayName(user: UserData): string {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
  return name || user.username
}

export function UsersManagement() {
  const navigate = useNavigate()
  const [users, setUsers] = useState<UserData[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [sortBy, setSortBy] = useState<SortOption>('lastActive')

  const fetchUsers = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (searchTerm.trim()) params.set('search', searchTerm.trim())
      params.set('page', String(page))
      params.set('per_page', String(PER_PAGE))
      const response = await fetch(`${API_BASE_URL}admin/users?${params}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })
      if (response.ok) {
        const data = await response.json()
        setUsers(data.users ?? [])
        setTotal(data.total ?? 0)
      }
    } catch (error) {
      console.error('Error fetching users:', error)
    } finally {
      setLoading(false)
    }
  }, [page, searchTerm])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput), 300)
    return () => clearTimeout(t)
  }, [searchInput])

  useEffect(() => {
    setPage(1)
  }, [searchTerm])

  const sortedUsers = [...users].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return getDisplayName(a).localeCompare(getDisplayName(b))
      case 'username':
        return a.username.localeCompare(b.username)
      case 'createdAt':
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      case 'chatCount':
        return b.chatCount - a.chatCount
      case 'messageCount':
        return b.messageCount - a.messageCount
      case 'lastActive': {
        if (a.lastActive && b.lastActive) {
          return new Date(b.lastActive).getTime() - new Date(a.lastActive).getTime()
        }
        if (a.lastActive && !b.lastActive) return -1
        if (!a.lastActive && b.lastActive) return 1
        return getDisplayName(a).localeCompare(getDisplayName(b))
      }
      default:
        return 0
    }
  })

  const cardStyle: React.CSSProperties = {
    background: 'var(--white)',
    border: '1px solid var(--bd)',
    borderRadius: 12,
    boxShadow: 'var(--sh-sm)',
    padding: 20,
  }
  const badgeGold: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    borderRadius: 100,
    padding: '2px 8px',
    fontSize: 10,
    fontWeight: 600,
    background: 'var(--gold-lt)',
    border: '1px solid var(--gold-bd)',
    color: 'var(--gold)',
  }
  const badgeRed: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 100,
    padding: '2px 8px',
    fontSize: 10,
    fontWeight: 600,
    background: 'rgba(239,68,68,.12)',
    border: '1px solid rgba(239,68,68,.25)',
    color: '#F87171',
  }
  const badgeMuted: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 100,
    padding: '2px 8px',
    fontSize: 10,
    fontWeight: 600,
    background: 'var(--bg2)',
    border: '1px solid var(--bd)',
    color: 'var(--muted)',
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={cardStyle}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 20, rowGap: 16 }}>
          <div style={{ flex: '1 1 280px', minWidth: 0 }}>
            <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, fontWeight: 500, color: 'var(--text)', margin: '0 0 4px' }}>
              User Management
            </h2>
            <p style={{ fontSize: 13, color: 'var(--muted)', margin: 0 }}>Search and view all registered users</p>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 12, flex: '1 1 320px', minWidth: 0 }}>
            <div style={{ flex: '1 1 200px', minWidth: 0 }}>
              <label htmlFor="search" style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--muted)', marginBottom: 6 }}>
                Search
              </label>
              <div style={{ position: 'relative' }}>
                <Search style={{ position: 'absolute', left: 12, top: '50%', marginTop: -8, width: 16, height: 16, color: 'var(--muted)' }} />
                <input
                  id="search"
                  placeholder="Name, username or email..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px 10px 36px',
                    fontSize: 14,
                    color: 'var(--text)',
                    background: 'var(--white)',
                    border: '1px solid var(--bd)',
                    borderRadius: 9,
                    outline: 'none',
                  }}
                />
              </div>
            </div>
            <div style={{ flex: '0 0 auto', minWidth: 160 }}>
              <label htmlFor="sort" style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--muted)', marginBottom: 6 }}>
                Sort by
              </label>
              <Select value={sortBy} onValueChange={(value: SortOption) => setSortBy(value)}>
                <SelectTrigger className="w-full" style={{ border: '1px solid var(--bd)', borderRadius: 9, height: 40 }}>
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Name</SelectItem>
                  <SelectItem value="username">Username</SelectItem>
                  <SelectItem value="createdAt">Join Date</SelectItem>
                  <SelectItem value="chatCount">Chat Count</SelectItem>
                  <SelectItem value="messageCount">Message Count</SelectItem>
                  <SelectItem value="lastActive">Last Active</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
          <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, fontWeight: 500, color: 'var(--text)', margin: 0 }}>
            All Users ({total})
          </h2>
          {total > 0 && (
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>
              Page {page} of {Math.ceil(total / PER_PAGE) || 1}
            </span>
          )}
        </div>
        {loading ? (
          <div style={{ height: 192, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: 32, height: 32, border: '2px solid var(--bd)', borderTopColor: 'var(--gold)', borderRadius: '50%', animation: 'spin .8s linear infinite' }} />
          </div>
        ) : sortedUsers.length > 0 ? (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--bd)' }}>
                    <th style={{ textAlign: 'left', padding: '12px 10px', fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase' }}>User</th>
                    <th style={{ textAlign: 'left', padding: '12px 10px', fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase' }}>Email</th>
                    <th style={{ textAlign: 'left', padding: '12px 10px', fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase' }}>Joined</th>
                    <th style={{ textAlign: 'left', padding: '12px 10px', fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase' }}>Last Active</th>
                    <th style={{ textAlign: 'center', padding: '12px 10px', fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase' }}>Chats</th>
                    <th style={{ textAlign: 'center', padding: '12px 10px', fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase' }}>Messages</th>
                    <th style={{ textAlign: 'right', padding: '12px 10px', fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', width: 100 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedUsers.map((user) => {
                    const displayName = getDisplayName(user)
                    const initial = displayName.trim().split(/\s+/).map(n => n[0]).join('').toUpperCase().slice(0, 2) || user.username.substring(0, 2).toUpperCase()
                    return (
                      <tr key={user.id} style={{ borderBottom: '1px solid var(--bd)' }}>
                        <td style={{ padding: '12px 10px', verticalAlign: 'middle' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div
                              style={{
                                width: 40,
                                height: 40,
                                borderRadius: 10,
                                background: 'var(--navy)',
                                color: 'var(--white)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 14,
                                fontWeight: 600,
                                flexShrink: 0,
                              }}
                            >
                              {initial}
                            </div>
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>{displayName}</span>
                                {user.role === 'admin' && (
                                  <span style={badgeGold}><Shield size={10} /> Admin</span>
                                )}
                                {user.isActive === false && <span style={badgeRed}>Deactivated</span>}
                                {user.lastActive && new Date(user.lastActive).toDateString() === new Date().toDateString() && (
                                  <span style={badgeMuted}>Active Today</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '12px 10px', fontSize: 13, color: 'var(--text)' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <Mail size={12} style={{ color: 'var(--muted)', flexShrink: 0 }} /> {user.email}
                          </span>
                        </td>
                        <td style={{ padding: '12px 10px', fontSize: 12, color: 'var(--muted)' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <Calendar size={12} style={{ flexShrink: 0 }} /> {formatISTDate(user.createdAt)}
                          </span>
                        </td>
                        <td style={{ padding: '12px 10px', fontSize: 12, color: 'var(--text)' }}>
                          {user.lastActive ? formatISTDate(user.lastActive) : 'Never'}
                        </td>
                        <td style={{ padding: '12px 10px', textAlign: 'center', fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
                          {user.chatCount}
                        </td>
                        <td style={{ padding: '12px 10px', textAlign: 'center', fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
                          {user.messageCount}
                        </td>
                        <td style={{ padding: '12px 10px', textAlign: 'right' }}>
                          <AdminPanelButton
                            type="button"
                            variant="primary"
                            size="sm"
                            onClick={() => navigate(`/admin/users/${user.id}`)}
                          >
                            <Eye size={12} /> View
                          </AdminPanelButton>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            {Math.ceil(total / PER_PAGE) > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--bd)' }}>
                <span style={{ fontSize: 13, color: 'var(--muted)' }}>
                  {total} user{total !== 1 ? 's' : ''} total
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <AdminPanelButton
                    type="button"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                  >
                    <ChevronLeft size={16} /> Prev
                  </AdminPanelButton>
                  <AdminPanelButton
                    type="button"
                    size="sm"
                    disabled={page >= Math.ceil(total / PER_PAGE)}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Next <ChevronRight size={16} />
                  </AdminPanelButton>
                </div>
              </div>
            )}
          </>
        ) : (
          <div style={{ height: 192, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontSize: 14 }}>
            {searchTerm ? 'No users found matching your search' : 'No users registered yet'}
          </div>
        )}
      </div>
    </div>
  )
}

